import type { LeaderboardRow } from '@str/api/types';
import { Link } from 'react-router';
import { channelsShort, kindLabel, waitingOn, whenLabel } from '@/lib/home';
import { postName } from '@/lib/post-title';
import { mediaStyle } from '@/lib/scenes';

const n = (v: number) => v.toLocaleString('en-US');

/**
 * Up to five posts by people reached, every channel added up; every row can be made again. A post
 * no channel has reported on is listed below the ranked ones with dashes and the reason, unranked.
 */
export function Leaderboard({
  rows,
  usual,
  onOpen,
  onMake,
  delay,
}: {
  rows: LeaderboardRow[];
  /** The "your usual" sentence for row 1, or the honest line before five posts. */
  usual: string | null;
  onOpen: (row: LeaderboardRow) => void;
  onMake: (row: LeaderboardRow) => void;
  delay: number;
}) {
  return (
    <table className="h-board h-rise" style={{ animationDelay: `${delay}ms` }}>
      <thead>
        <tr>
          <th scope="col" colSpan={3}>
            Post
          </th>
          <th scope="col" className="num">
            Reached
          </th>
          <th scope="col" className="num">
            Engaged
          </th>
          <th scope="col" className="num">
            Clicks
          </th>
          <th scope="col" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.id}
            className="hv"
            tabIndex={0}
            onClick={() => onOpen(r)}
            onKeyDown={(e) => e.key === 'Enter' && onOpen(r)}
          >
            <td className="rank" aria-label={r.rank === null ? 'Not ranked yet' : undefined}>
              {r.rank ?? ''}
            </td>
            <td className="pic">
              <span style={mediaStyle({ url: r.imageUrl, scene: 'dusk' })} />
            </td>
            <td className="what">
              <span className="title">{postName(r)}</span>
              <span className="meta">
                {kindLabel(r)} · {whenLabel(r.publishedAt, r.scheduledTime, r.timezone)} ·{' '}
                {channelsShort(r.channels)}
              </span>
              {r.rank === 1 && usual && <span className="usual">{usual}</span>}
              {!r.measured && (
                <span className="usual">
                  {r.unavailable[0]
                    ? `Not available yet · ${waitingOn(r.unavailable[0].channel)}`
                    : 'Not available yet · no channel has reported numbers'}
                </span>
              )}
            </td>
            {/* Not measured is not zero: a dash until a channel reports. */}
            <td className="num">{r.measured ? n(r.reached) : '—'}</td>
            <td className="num">{r.measured ? n(r.engaged) : '—'}</td>
            <td className="num">{n(r.clicks)}</td>
            <td className="act">
              <button
                type="button"
                className="btn btn-ghost hv"
                onClick={(e) => {
                  e.stopPropagation();
                  onMake(r);
                }}
              >
                Make another like this
              </button>
            </td>
          </tr>
        ))}
        {rows.length < 5 && (
          <tr className="next">
            <td className="rank">{rows.length + 1}</td>
            <td className="pic">
              <span className="dashed" />
            </td>
            <td className="what" colSpan={4}>
              <span className="title muted">Your next post lands here.</span>
            </td>
            <td className="act">
              <Link to="/app/create" className="btn btn-ghost hv">
                Make a post
              </Link>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
